import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { describeError, useToast } from "../components/toast";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import GroupWorkOutlinedIcon from "@mui/icons-material/GroupWorkOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { foyerApi } from "../http/foyer";
import {
  PermissionGate,
  companyPlace,
  useHomePlace,
  usePermission,
} from "../auth/access";
import { tokens } from "../theme";
import { ItemFormSurface } from "../components/EditDrawer";
import { platformLabel } from "../utils/devicePresentation";
import type { Translate } from "../i18n/translate";
import { PageContainer, PageHeader } from "../components/PageHeader";
import HelpBadge from "../components/HelpBadge";
import type {
  DeviceGroup,
  DevicePlatform,
  FleetDevice,
} from "@shared/types/foyer";

function deviceLabel(d: FleetDevice): string {
  const where = [d.roomId, d.premiseName].filter(Boolean).join(" · ");
  return where || d.id;
}

function premiseKey(t: Translate, d: FleetDevice): string {
  return (
    d.premiseName || d.premiseId || t("signage:deviceGroupsPage.unassigned")
  );
}

/** A small green/grey dot conveying live online status. */
function StatusDot({ online }: { online: boolean }) {
  const { t } = useTranslation("signage");
  return (
    <Box
      component="span"
      role="img"
      aria-label={
        online ? t("deviceGroupsPage.online") : t("deviceGroupsPage.offline")
      }
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        flexShrink: 0,
        bgcolor: online ? tokens.success : tokens.inkMuted,
        opacity: online ? 1 : 0.45,
      }}
    />
  );
}

/** "{online}/{total} online" as a traffic-light tinted chip. */
function OnlineRollupChip({
  online,
  total,
}: {
  online: number;
  total: number;
}) {
  const { t } = useTranslation(["signage", "premises"]);
  if (total === 0) {
    return (
      <Chip
        size="small"
        label={t("deviceGroupsPage.noDevices")}
        sx={{
          bgcolor: tokens.rowAlt,
          color: tokens.inkMuted,
          fontWeight: "fontWeightSemibold",
        }}
      />
    );
  }
  const allOn = online === total;
  const noneOn = online === 0;
  const bg = allOn
    ? tokens.successTint
    : noneOn
      ? tokens.rowAlt
      : tokens.warningTint;
  const fg = allOn ? tokens.success : noneOn ? tokens.inkMuted : tokens.warning;
  return (
    <Chip
      size="small"
      label={t("deviceGroupsPage.onlineOfTotal", { online, total })}
      sx={{ bgcolor: bg, color: fg, fontWeight: "fontWeightSemibold" }}
    />
  );
}

/** The create/edit dialog: name, description, and a premise-grouped device picker. */
function GroupDialog({
  open,
  initial,
  devices,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: DeviceGroup | null;
  devices: FleetDevice[];
  onClose: () => void;
  /** Resolves true when saved: the editor then closes itself (onClose). */
  onSave: (v: {
    name: string;
    description: string;
    screenIds: string[];
  }) => Promise<boolean>;
}) {
  const { t } = useTranslation(["signage", "common", "premises"]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const home = useHomePlace();
  const savePlace = initial ? companyPlace(initial.companyId) : home;
  const saveLevel =
    savePlace === "nenda" ? ("administrate" as const) : ("edit" as const);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [plat, setPlat] = useState<DevicePlatform | "all">("all");

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setDescription(initial?.description ?? "");
    setSelected(new Set(initial?.screenIds ?? []));
    setQ("");
    setPlat("all");
  }, [open, initial]);

  // Platforms actually present in the fleet, for the quick-filter row.
  const platforms = useMemo(() => {
    const set = new Set<DevicePlatform>();
    for (const d of devices) if (d.platform) set.add(d.platform);
    return [...set].sort();
  }, [devices]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    return devices.filter((d) => {
      if (plat !== "all" && d.platform !== plat) return false;
      if (!needle) return true;
      return [d.roomId, d.premiseName, d.model, d.id].some((v) =>
        v?.toLowerCase().includes(needle)
      );
    });
  }, [devices, q, plat]);

  // Group the visible devices by premise (sorted), so the picker reads as a
  // site map rather than a flat list.
  const groupedByPremise = useMemo(() => {
    const map = new Map<string, FleetDevice[]>();
    for (const d of filtered) {
      const key = premiseKey(t, d);
      const arr = map.get(key);
      if (arr) arr.push(d);
      else map.set(key, [d]);
    }
    return [...map.entries()]
      .map(([premise, list]) => ({
        premise,
        list: [...list].sort((a, b) =>
          deviceLabel(a).localeCompare(deviceLabel(b))
        ),
      }))
      .sort((a, b) => a.premise.localeCompare(b.premise));
  }, [filtered, t]);

  const filteredIds = useMemo(() => filtered.map((d) => d.id), [filtered]);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const selectedOnline = useMemo(
    () =>
      devices.reduce((n, d) => n + (selected.has(d.id) && d.online ? 1 : 0), 0),
    [devices, selected]
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function setMany(ids: string[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  return (
    <ItemFormSurface
      mode={initial ? "edit" : "create"}
      open={open}
      onClose={onClose}
      title={
        initial
          ? t("deviceGroupsPage.editGroup")
          : t("deviceGroupsPage.newDeviceGroup")
      }
      maxWidth="sm"
      drawerWidth={640}
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate
            resource="screens"
            level={saveLevel}
            place={savePlace}
            deniedProps={{ disabled: true }}
          >
            <Button
              variant="contained"
              disabled={!name.trim()}
              onClick={() =>
                void onSave({
                  name: name.trim(),
                  description: description.trim(),
                  screenIds: [...selected],
                }).then((ok) => ok && dismiss())
              }
            >
              {t("common:buttons.save")}
            </Button>
          </PermissionGate>
        </>
      )}
    >
      <Stack spacing={2.5} sx={{ mt: 1 }}>
        <TextField
          label={t("common:labels.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          autoFocus
        />
        <TextField
          label={t("common:labels.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
        />
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2">
              {t("deviceGroupsPage.members")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selected.size > 0
                ? t("deviceGroupsPage.selectedWithOnline", {
                    count: selected.size,
                    online: selectedOnline,
                  })
                : t("deviceGroupsPage.selected", { count: selected.size })}
            </Typography>
          </Stack>

          <TextField
            size="small"
            fullWidth
            placeholder={t("deviceGroupsPage.searchDevicesPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            inputProps={{
              "aria-label": t("deviceGroupsPage.searchDevicesAria"),
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />

          {platforms.length > 1 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}
            >
              <Chip
                size="small"
                label={t("deviceGroupsPage.all")}
                variant={plat === "all" ? "filled" : "outlined"}
                color={plat === "all" ? "primary" : "default"}
                onClick={() => setPlat("all")}
              />
              {platforms.map((p) => (
                <Chip
                  key={p}
                  size="small"
                  label={platformLabel(p)}
                  variant={plat === p ? "filled" : "outlined"}
                  color={plat === p ? "primary" : "default"}
                  onClick={() => setPlat(p)}
                />
              ))}
            </Stack>
          )}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary">
              {t("deviceGroupsPage.devicesShown", { count: filtered.length })}
            </Typography>
            <Button
              size="small"
              disabled={filteredIds.length === 0}
              onClick={() => setMany(filteredIds, !allFilteredSelected)}
            >
              {allFilteredSelected
                ? t("common:buttons.clear")
                : plat === "all"
                  ? t("deviceGroupsPage.selectAllShown")
                  : t("deviceGroupsPage.selectAllPlatform", {
                      platform: platformLabel(plat),
                    })}
            </Button>
          </Stack>

          <List
            dense
            subheader={<li />}
            sx={{
              maxHeight: 320,
              overflow: "auto",
              border: `1px solid ${tokens.rowAlt}`,
              borderRadius: 1,
              "& ul": { p: 0 },
            }}
          >
            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {t("deviceGroupsPage.noDevicesMatch")}
              </Typography>
            )}
            {groupedByPremise.map(({ premise, list }) => {
              const ids = list.map((d) => d.id);
              const onlineCount = list.filter((d) => d.online).length;
              const selCount = ids.filter((id) => selected.has(id)).length;
              const allSel = selCount === ids.length;
              const someSel = selCount > 0 && !allSel;
              return (
                <li key={premise}>
                  <ul>
                    <ListSubheader
                      disableSticky={false}
                      sx={{
                        bgcolor: tokens.surface,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        lineHeight: "40px",
                      }}
                    >
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={allSel}
                        indeterminate={someSel}
                        onChange={(e) => setMany(ids, e.target.checked)}
                        inputProps={{
                          "aria-label": t("deviceGroupsPage.selectAllIn", {
                            premise,
                          }),
                        }}
                        sx={{ p: 0.5 }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{ flex: 1, minWidth: 0 }}
                        noWrap
                      >
                        {premise}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("deviceGroupsPage.onlineOfTotal", {
                          online: onlineCount,
                          total: ids.length,
                        })}
                      </Typography>
                    </ListSubheader>
                    {list.map((d) => (
                      <ListItemButton
                        key={d.id}
                        onClick={() => toggle(d.id)}
                        dense
                      >
                        <Checkbox
                          edge="start"
                          checked={selected.has(d.id)}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                          inputProps={{ "aria-label": deviceLabel(d) }}
                        />
                        <StatusDot online={d.online} />
                        <ListItemText
                          sx={{ ml: 1 }}
                          primary={deviceLabel(d)}
                          secondary={
                            [platformLabel(d.platform), d.model]
                              .filter((v) => v && v !== "-")
                              .join(" · ") || undefined
                          }
                        />
                      </ListItemButton>
                    ))}
                  </ul>
                </li>
              );
            })}
          </List>
        </Box>
      </Stack>
    </ItemFormSurface>
  );
}

/**
 * One group as a card, with online rollup and member chips. Clicking the name
 * opens the same edit drawer as the pencil - renaming lives there, not inline
 * (the CMS rule: edits happen in the right-hand drawer).
 */
function GroupCard({
  group,
  nameById,
  onlineById,
  onReboot,
  onMessage,
  onEdit,
  onDelete,
}: {
  group: DeviceGroup;
  nameById: Map<string, string>;
  onlineById: Map<string, boolean>;
  onReboot: () => void;
  onMessage: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation(["signage", "common"]);
  const place = companyPlace(group.companyId);
  const groupLevel = group.companyId ? "edit" : "administrate";
  const canEditScreens = usePermission("screens", groupLevel, place);
  const canMessageAtPlace = usePermission("screens/messages", "edit", place);
  const canMessageScreens = canMessageAtPlace && canEditScreens;
  const total = group.screenIds.length;
  const online = group.screenIds.reduce(
    (n, id) => n + (onlineById.get(id) ? 1 : 0),
    0
  );
  const memberNames = group.screenIds.map((id) => nameById.get(id) ?? id);
  const shown = memberNames.slice(0, 6);
  const extra = memberNames.length - shown.length;

  return (
    <Card sx={{ p: 2, display: "flex", alignItems: "flex-start", gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Tooltip title={t("deviceGroupsPage.editGroup")}>
            <Typography
              variant="subtitle1"
              component="button"
              onClick={onEdit}
              sx={{
                fontWeight: "fontWeightSemibold",
                cursor: "pointer",
                border: 0,
                bgcolor: "transparent",
                font: "inherit",
                color: "inherit",
                textAlign: "left",
                borderRadius: 1,
                px: 0.5,
                mx: -0.5,
                "&:hover": { bgcolor: tokens.rowAlt },
              }}
            >
              {group.name}
            </Typography>
          </Tooltip>
          <OnlineRollupChip online={online} total={total} />
        </Stack>

        {group.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {group.description}
          </Typography>
        )}

        {total === 0 ? (
          <Typography variant="body2" sx={{ color: tokens.warning }}>
            {t("deviceGroupsPage.noDevicesYet")}
          </Typography>
        ) : (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ flexWrap: "wrap", gap: 0.75, mt: 0.5 }}
          >
            {shown.map((label, i) => (
              <Chip
                key={`${group.screenIds[i]}-${i}`}
                size="small"
                variant="outlined"
                label={label}
                sx={{ maxWidth: 220 }}
              />
            ))}
            {extra > 0 && (
              <Chip
                size="small"
                label={t("deviceGroupsPage.moreCount", { count: extra })}
                sx={{ bgcolor: tokens.rowAlt, color: tokens.inkMuted }}
              />
            )}
          </Stack>
        )}
      </Box>

      <Stack direction="row">
        {canEditScreens && (
          <Tooltip
            title={
              total === 0
                ? t("deviceGroupsPage.noDevicesToReboot")
                : t("deviceGroupsPage.rebootGroup")
            }
          >
            <span>
              <IconButton
                aria-label={t("deviceGroupsPage.rebootGroup")}
                disabled={total === 0}
                onClick={onReboot}
              >
                <RestartAltIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {canMessageScreens && (
          <Tooltip
            title={
              total === 0
                ? t("deviceGroupsPage.noDevicesToMessage")
                : t("deviceGroupsPage.messageGroup")
            }
          >
            <span>
              <IconButton
                aria-label={t("deviceGroupsPage.messageGroup")}
                disabled={total === 0}
                onClick={onMessage}
              >
                <CampaignOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <PermissionGate resource="screens" level={groupLevel} place={place}>
          <Tooltip title={t("deviceGroupsPage.editGroup")}>
            <IconButton
              aria-label={t("deviceGroupsPage.editGroup")}
              onClick={onEdit}
            >
              <EditOutlinedIcon />
            </IconButton>
          </Tooltip>
        </PermissionGate>
        <PermissionGate resource="screens" level={groupLevel} place={place}>
          <Tooltip title={t("deviceGroupsPage.deleteGroup")}>
            <IconButton
              aria-label={t("deviceGroupsPage.deleteGroup")}
              onClick={onDelete}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </PermissionGate>
      </Stack>
    </Card>
  );
}

/** Compose a reception message to push to every device in a group. */
function MessageDialog({
  group,
  onClose,
  onSend,
}: {
  group: DeviceGroup | null;
  onClose: () => void;
  onSend: (v: { title: string; body: string; durationMs: number }) => void;
}) {
  const { t } = useTranslation(["signage", "common", "premises"]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [seconds, setSeconds] = useState(8);

  useEffect(() => {
    if (group) {
      setTitle("");
      setBody("");
      setSeconds(8);
    }
  }, [group]);

  const total = group?.screenIds.length ?? 0;

  return (
    <Dialog open={!!group} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {t("deviceGroupsPage.messageTitle", { name: group?.name })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t("deviceGroupsPage.shownOn", { count: total })}
          </Typography>
          <TextField
            label={t("deviceGroupsPage.titleOptional")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label={t("deviceGroupsPage.message")}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ "aria-label": t("deviceGroupsPage.messageBodyAria") }}
          />
          <TextField
            label={t("deviceGroupsPage.durationSeconds")}
            type="number"
            value={seconds}
            onChange={(e) =>
              setSeconds(
                Math.max(1, Math.min(120, Number(e.target.value) || 1))
              )
            }
            sx={{ width: 180 }}
            inputProps={{ min: 1, max: 120 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common:buttons.cancel")}</Button>
        <Button
          variant="contained"
          disabled={!body.trim()}
          onClick={() =>
            onSend({
              title: title.trim(),
              body: body.trim(),
              durationMs: seconds * 1000,
            })
          }
        >
          {t("common:buttons.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type SortKey = "name" | "members" | "updated";

export default function DeviceGroupsPage() {
  const [groups, setGroups] = useState<DeviceGroup[]>([]);
  const [devices, setDevices] = useState<FleetDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DeviceGroup | null>(null);
  const [messaging, setMessaging] = useState<DeviceGroup | null>(null);
  const toast = useToast();
  const { t } = useTranslation(["signage", "common", "glossary"]);
  const navigate = useNavigate();
  const home = useHomePlace();
  const newGroupLevel = home === "nenda" ? "administrate" : "edit";
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("name");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, f] = await Promise.all([
        foyerApi.listDeviceGroups(),
        foyerApi.fleetDevices(),
      ]);
      setGroups(g.groups);
      setDevices(f.devices);
    } catch {
      setError(t("deviceGroupsPage.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const nameById = useMemo(
    () => new Map(devices.map((d) => [d.id, deviceLabel(d)])),
    [devices]
  );
  const onlineById = useMemo(
    () => new Map(devices.map((d) => [d.id, d.online])),
    [devices]
  );

  const visibleGroups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = groups.filter(
      (g) =>
        !needle ||
        g.name.toLowerCase().includes(needle) ||
        g.description?.toLowerCase().includes(needle)
    );
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sort === "members") return b.screenIds.length - a.screenIds.length;
      if (sort === "updated")
        return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
      return a.name.localeCompare(b.name);
    });
    return sorted;
  }, [groups, query, sort]);

  async function save(v: {
    name: string;
    description: string;
    screenIds: string[];
  }): Promise<boolean> {
    const adding = !editing;
    try {
      if (editing) {
        await foyerApi.updateDeviceGroup(editing.groupId, v);
      } else {
        await foyerApi.createDeviceGroup(v);
      }
      toast.success(
        adding
          ? t("deviceGroupsPage.created", {
              name: v.name,
              count: v.screenIds.length,
            })
          : t("deviceGroupsPage.updated", { name: v.name })
      );
      await load();
      // The editor slides out, then its onClose sets dialogOpen false.
      return true;
    } catch (err) {
      // The editor stays open with what was typed.
      toast.error(
        describeError(
          t,
          adding ? "createDeviceGroup" : "saveDeviceGroup",
          err,
          { name: v.name }
        )
      );
      return false;
    }
  }

  async function remove(g: DeviceGroup) {
    if (!window.confirm(t("deviceGroupsPage.confirmDelete", { name: g.name })))
      return;
    try {
      await foyerApi.deleteDeviceGroup(g.groupId);
      toast.success(t("deviceGroupsPage.deleted", { name: g.name }));
      await load();
    } catch (err) {
      toast.error(describeError(t, "deleteDeviceGroup", err, { name: g.name }));
    }
  }

  async function reboot(g: DeviceGroup) {
    const n = g.screenIds.length;
    if (n === 0) return;
    if (
      !window.confirm(
        t("deviceGroupsPage.confirmReboot", { count: n, name: g.name })
      )
    )
      return;
    try {
      const res = await foyerApi.rebootGroup(g.groupId);
      toast.success(
        t("deviceGroupsPage.rebootDispatched", {
          count: res.count,
          name: g.name,
        }),
        res.rolloutId
          ? {
              action: {
                label: t("deviceGroupsPage.viewRollout"),
                onClick: () => navigate("/ota"),
              },
            }
          : undefined
      );
    } catch (err) {
      toast.error(describeError(t, "rebootDeviceGroup", err, { name: g.name }));
    }
  }

  async function sendMessage(
    g: DeviceGroup,
    v: { title: string; body: string; durationMs: number }
  ) {
    try {
      const res = await foyerApi.messageGroup({
        id: g.groupId,
        title: v.title || undefined,
        body: v.body,
        durationMs: v.durationMs,
      });
      setMessaging(null);
      toast.success(
        t("deviceGroupsPage.messageSent", { count: res.count, name: g.name }),
        res.rolloutId
          ? {
              action: {
                label: t("deviceGroupsPage.viewRollout"),
                onClick: () => navigate("/ota"),
              },
            }
          : undefined
      );
    } catch (err) {
      toast.error(
        describeError(t, "sendMessageToDeviceGroup", err, { name: g.name })
      );
    }
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("deviceGroupsPage.eyebrow")}
        title={t("deviceGroupsPage.title")}
        actions={
          <PermissionGate resource="screens" level={newGroupLevel} place={home}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              {t("deviceGroupsPage.newGroup")}
            </Button>
          </PermissionGate>
        }
      />

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      <Card variant="outlined" sx={{ position: "relative", mt: 3 }}>
        <HelpBadge title={t("deviceGroupsPage.help")} />
        <CardContent>
          {!loading && groups.length > 0 && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ mb: 2, pr: { xs: 0, sm: 4 } }}
            >
              <TextField
                size="small"
                placeholder={t("deviceGroupsPage.searchGroupsPlaceholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                inputProps={{
                  "aria-label": t("deviceGroupsPage.searchGroupsAria"),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, maxWidth: { sm: 360 } }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t("deviceGroupsPage.sortBy")}</InputLabel>
                <Select
                  label={t("deviceGroupsPage.sortBy")}
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                >
                  <MenuItem value="name">{t("common:labels.name")}</MenuItem>
                  <MenuItem value="members">
                    {t("deviceGroupsPage.sortMembers")}
                  </MenuItem>
                  <MenuItem value="updated">
                    {t("deviceGroupsPage.sortUpdated")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}

          {loading ? (
            <Stack spacing={1.5}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    p: 2,
                    height: 96,
                    borderRadius: 1,
                    bgcolor: tokens.rowAlt,
                    opacity: 0.5,
                  }}
                />
              ))}
            </Stack>
          ) : groups.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: tokens.inkMuted }}>
              <GroupWorkOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              <Typography sx={{ mt: 1 }}>
                {t("deviceGroupsPage.empty")}
              </Typography>
              <Typography variant="body2">
                {t("deviceGroupsPage.emptyHint")}
              </Typography>
            </Box>
          ) : visibleGroups.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: tokens.inkMuted }}>
              <Typography>
                {t("deviceGroupsPage.noMatch", { query })}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {visibleGroups.map((g) => (
                <GroupCard
                  key={g.groupId}
                  group={g}
                  nameById={nameById}
                  onlineById={onlineById}
                  onReboot={() => reboot(g)}
                  onMessage={() => setMessaging(g)}
                  onEdit={() => {
                    setEditing(g);
                    setDialogOpen(true);
                  }}
                  onDelete={() => remove(g)}
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <GroupDialog
        open={dialogOpen}
        initial={editing}
        devices={devices}
        // `editing` is left as-is on close: clearing it mid-slide would flip
        // the drawer into the create modal. "New group" resets it on open.
        onClose={() => setDialogOpen(false)}
        onSave={save}
      />

      <MessageDialog
        group={messaging}
        onClose={() => setMessaging(null)}
        onSend={(v) => {
          if (messaging) void sendMessage(messaging, v);
        }}
      />
    </PageContainer>
  );
}
